import { useState, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { patchSessionSuccess } from '../App/actions'
import '../../fonts/fonts.css'
import graduationIcon    from '../../images/icon-graduation.png'
import logoTgdEval       from '../../images/logo-tgd-eval.png'
import logoTgdMeteo       from '../../images/logo-tgd-meteo.png'
import logoTgdDeclivite       from '../../images/logo-tgd-declivite.png'
import logoTgdRemc       from '../../images/logo-tgd-remc.png'
import logoTgdEco        from '../../images/logo-tgd-eco.png'
import logoTgdPasserelle from '../../images/logo-tgd-passerelle.png'
import logoTgdPostPermis from '../../images/logo-tgd-post-permis.png'
import closeRedIcon     from '../../images/closeRed.svg'
import searchIcon       from '../../images/search.svg'
import resetIcon        from '../../images/delete.svg'
import {
  Wrapper,
  PageHeader, PageIconBox, PageIconImg, PageTitle, PageSubtitle,
  CardsArea, ItemCard, ItemLogo, ItemInfoBlock, ItemCategory, ItemName, ItemQtyBlock, ItemQtyLine, ItemSpacer, AttribuerBtn, AcheterBtn,
  EmptyRow, EmptyIcon, EmptyTitle, EmptySubtitle,
  ModalOverlay, ModalPanel, ModalCloseButton,
  ModalColHeaders, ColHeaderBox, ColHeaderText,
  ModalSearchRow, ModalSearchBar, ModalSearchInput, ModalSearchSpacer, ModalSearchBtn,
  ModalBody, ModalRow, ModalCell, ModalToggleCell,
  Checkbox, TooltipAnchor, TooltipBubble,
  ModalEmptyRow,
  PlayerCellWrapper, InitialBubble,
} from './styledComponents'

const apiBase = process.env.REACT_APP_TGDAPI_SERVER || ''

const SEARCH_PAGE_SIZE = 50

// L'API fait une recherche regex : on échappe les caractères spéciaux saisis
const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Mêmes paramètres que le filtre du tableau des élèves (SimuPlayers/saga.js)
const buildStudentsUrl = (name, email) => {
  const params = []
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length > 1) {
    params.push(`firstname=${encodeURIComponent(escapeRegex(words[0]))}`)
    params.push(`lastname=${encodeURIComponent(escapeRegex(words[1]))}`)
  } else if (words.length === 1) {
    params.push(`firstname=${encodeURIComponent(escapeRegex(words[0]))}`)
  }
  if (email.trim()) params.push(`email=${encodeURIComponent(escapeRegex(email.trim()))}`)
  params.push(`sortName=dateCreation&sortType=desc&pageSize=${SEARCH_PAGE_SIZE}&pageIndex=0`)
  params.push('status=noArchivedStudents')
  return `${apiBase}/students?${params.join('&')}`
}

const getInitials = (firstname = '', lastname = '') => {
  return ((firstname[0] || '') + (lastname[0] || '')).toUpperCase() || '?'
}

const getFormationMeta = (formation) => {
  const { category, title } = formation
  const t = (title || '').toLowerCase()
  if (category === 'Eval') return { color: '#6462FC', label: 'ÉVALUATION ET BILAN', logo: logoTgdEval }
  if (category === 'Hardware') return { color: '#00C8F8', label: 'MATÉRIEL', logo: logoTgdRemc }
  if (category === 'Conduite') {
    if (t.includes('météo') || t.includes('meteo')) return { color: '#3494FA', label: 'MÉTÉO', logo: logoTgdMeteo }
    if (t.includes('déclivité') || t.includes('declivite')) return { color: '#3494FA', label: 'DÉCLIVITÉ', logo: logoTgdDeclivite }
    if (t.includes('éco') || t.includes('eco')) return { color: '#3494FA', label: 'ÉCO-CONDUITE', logo: logoTgdEco }
    if (t.includes('post permis') || t.includes('post-permis')) return { color: '#3494FA', label: 'POST PERMIS', logo: logoTgdPostPermis }
    if (t.includes('passerelle')) return { color: '#3494FA', label: 'PASSERELLE', logo: logoTgdPasserelle }
    return { color: '#3494FA', label: 'CONDUITE', logo: logoTgdRemc }
  }
  return { color: '#6462FC', label: (category || 'FORMATION').toUpperCase(), logo: logoTgdRemc }
}

const MyFormations = () => {
  const dispatch    = useDispatch()
  const agencyToken = useSelector(state => state.toJS().global.agencytoken) || ''
  const sessions    = useSelector(state => state.toJS().global.sessions  || [])
  const orders      = useSelector(state => state.toJS().global.orders    || [])
  const loading     = useSelector(state => state.toJS().global.sessions == null)
  const history     = useHistory()

  // Modal state
  const [activeGroup, setActiveGroup]   = useState(null)
  const [nameSearch, setNameSearch]     = useState('')
  const [emailSearch, setEmailSearch]   = useState('')
  const [players, setPlayers]           = useState([])
  const [searching, setSearching]       = useState(false)
  const [searchError, setSearchError]   = useState('')
  const searchRequestId = useRef(0)

  const orderMap = useMemo(() => {
    const map = {}
    orders.forEach(o => { map[o._id] = o })
    return map
  }, [orders])

  // ── Group by product (from orders), overlay sessions ───────────
  // Clé = productId : deux produits de même titre, ou un produit renommé, ne se mélangent pas
  const grouped = useMemo(() => {
    const map = {}
    const groupKey = (item) => item.productId || item.name

    // 1. Build formation list from paid orders (le titre le plus récent l'emporte)
    Object.values(orderMap)
      .filter(order => order.status === 'completed')
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .forEach(order => {
        ;(order.items || []).forEach(item => {
          const key = groupKey(item)
          if (!key || !item.name) return
          map[key] = {
            key,
            formation: { title: item.name, category: item.category || 'Conduite' },
            sessions:  map[key] ? map[key].sessions : [],
            productId: item.productId || null,
          }
        })
      })

    // 2. Attach sessions to their formation
    sessions.forEach(session => {
      const order = orderMap[session.orderId]
      if (!order) return
      const item  = order.items?.[session.orderItemIndex]
      const key   = item && groupKey(item)
      if (!key || !map[key]) return
      map[key].sessions.push(session)
    })

    return Object.values(map).map(g => ({
      ...g,
      available:  g.sessions.filter(s => !s.studentId).length,
      assigned:   g.sessions.filter(s =>  s.studentId).length,
    }))
  }, [sessions, orderMap])

  // Recompute activeGroup from grouped when sessions change
  const currentGroup = activeGroup
    ? grouped.find(g => g.key === activeGroup.key) ?? null
    : null

  // ── Student search for modal (API : le store ne contient qu'une page d'élèves) ──
  const searchPlayers = async (name, email) => {
    const requestId = ++searchRequestId.current
    setSearching(true)
    setSearchError('')
    try {
      const resp = await fetch(buildStudentsUrl(name, email), {
        headers: { 'Authorization': `Bearer ${agencyToken}` },
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      if (requestId !== searchRequestId.current) return
      setPlayers(Array.isArray(data) ? data : [])
    } catch (e) {
      if (requestId !== searchRequestId.current) return
      console.error('[MyFormations] student search error:', e)
      setPlayers([])
      setSearchError('La recherche des élèves a échoué.')
    } finally {
      if (requestId === searchRequestId.current) setSearching(false)
    }
  }

  const handleSearch = () => searchPlayers(nameSearch, emailSearch)
  const handleResetSearch = () => {
    setNameSearch('')
    setEmailSearch('')
    searchPlayers('', '')
  }
  const handleSearchKeyDown = (e) => { if (e.key === 'Enter') handleSearch() }

  const openModal = (group) => {
    setActiveGroup(group)
    setNameSearch('')
    setEmailSearch('')
    setPlayers([])
    searchPlayers('', '')
  }
  const closeModal = () => setActiveGroup(null)

  // ── Step 1 : assign a session to a student ────────────────────
  const assignSession = async (playerId) => {
    if (!currentGroup) return

    // Find first unassigned session for this formation
    const session = currentGroup.sessions.find(s => !s.studentId)
    if (!session) return

    const sid = session._id
    try {
      const resp = await fetch(`${apiBase}/sessions/${sid}`, {
        method:  'PATCH',
        headers: { 'Authorization': `Bearer ${agencyToken}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ studentId: playerId }),
      })
      if (!resp.ok) {
        console.error('[MyFormations] assign failed:', resp.status, await resp.text().catch(() => ''))
        return
      }
      dispatch(patchSessionSuccess({ ...session, studentId: playerId }))
    } catch (e) {
      console.error('[MyFormations] assign error:', e)
    }
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <Wrapper>
      <PageHeader>
        <PageIconBox>
          <PageIconImg src={graduationIcon} alt='' />
        </PageIconBox>
        <div>
          <PageTitle>Mes formations</PageTitle>
          <PageSubtitle>Toutes vos formations visibles en un coup d'oeil</PageSubtitle>
        </div>
      </PageHeader>

      <CardsArea>
        {loading ? (
          <EmptyRow><EmptyTitle>Chargement de vos formations…</EmptyTitle></EmptyRow>
        ) : grouped.length === 0 ? (
          <EmptyRow>
            <EmptyIcon>🎓</EmptyIcon>
            <EmptyTitle>Aucune formation achetée</EmptyTitle>
            <EmptySubtitle>Vos formations apparaîtront ici après votre premier achat dans le Magasin.</EmptySubtitle>
          </EmptyRow>
        ) : (
          grouped.map(group => {
            const { color, label, logo } = getFormationMeta(group.formation)
            return (
              <ItemCard key={group.key}>
                <ItemLogo><img src={logo} alt='' /></ItemLogo>
                <ItemInfoBlock>
                  <ItemCategory color={color}>{label}</ItemCategory>
                  {/* <ItemName>{group.formation.title}</ItemName> */}
                </ItemInfoBlock>
                <ItemQtyBlock>
                  <ItemQtyLine>Sessions disponibles : {group.available}</ItemQtyLine>
                  <ItemQtyLine>Sessions attribuées : {group.assigned}</ItemQtyLine>
                </ItemQtyBlock>
                <ItemSpacer />
                {group.available === 0 && group.productId && (
                  <AcheterBtn onClick={() => history.push(`/store/${group.productId}`)}>
                    Acheter
                  </AcheterBtn>
                )}
                <AttribuerBtn
                  disabled={group.available <= 0}
                  onClick={() => group.available > 0 && openModal(group)}
                >
                  Attribuer
                </AttribuerBtn>
              </ItemCard>
            )
          })
        )}
      </CardsArea>

      {currentGroup && (
        <ModalOverlay onClick={closeModal}>
          <ModalPanel onClick={e => e.stopPropagation()}>
            <ModalCloseButton type='button' onClick={closeModal} aria-label='Fermer la fenêtre'>
              <img src={closeRedIcon} alt='' />
            </ModalCloseButton>

            <ModalColHeaders>
              <ColHeaderBox><ColHeaderText>Élèves</ColHeaderText></ColHeaderBox>
              <ColHeaderBox><ColHeaderText>Email</ColHeaderText></ColHeaderBox>
              <ColHeaderBox fixed><ColHeaderText>Attribuer ?</ColHeaderText></ColHeaderBox>
            </ModalColHeaders>

            <ModalSearchRow>
              <ModalSearchBar>
                <ModalSearchInput
                  value={nameSearch}
                  onChange={e => setNameSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder='Rechercher un·e élève par nom'
                />
              </ModalSearchBar>
              <ModalSearchBar>
                <ModalSearchInput
                  value={emailSearch}
                  onChange={e => setEmailSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder='Rechercher un·e élève par email'
                />
              </ModalSearchBar>
              <ModalSearchSpacer>
                <ModalSearchBtn type='button' onClick={handleSearch} disabled={searching} title='Rechercher' aria-label='Rechercher'>
                  <img src={searchIcon} alt='' />
                </ModalSearchBtn>
                <ModalSearchBtn type='button' onClick={handleResetSearch} disabled={searching} title='Réinitialiser' aria-label='Réinitialiser la recherche'>
                  <img src={resetIcon} alt='' />
                </ModalSearchBtn>
              </ModalSearchSpacer>
            </ModalSearchRow>

            <ModalBody>
              {searching ? (
                <ModalEmptyRow>Recherche en cours…</ModalEmptyRow>
              ) : searchError ? (
                <ModalEmptyRow>{searchError}</ModalEmptyRow>
              ) : players.length === 0 ? (
                <ModalEmptyRow>
                  {nameSearch.trim() || emailSearch.trim()
                    ? 'Aucun élève ne correspond à la recherche.'
                    : 'Aucun élève trouvé dans votre agence.'}
                </ModalEmptyRow>
              ) : (
                players.map((player, index) => {
                  const uid      = player.id || player._id || player.email || index
                  const assigned = currentGroup.sessions.some(s => s.studentId === uid)
                  const canAdd   = !assigned && currentGroup.available > 0
                  const assignedTooltip = assigned
                    ? "Pour retirer l'attribution de l'élève, rendez vous sur sa page profil, puis allez dans l'onglet Formations"
                    : undefined
                  return (
                    <ModalRow key={uid}>
                      <ModalCell
                        clickable
                        onClick={() => { closeModal(); history.push(`/simuplayers/trainings/${uid}`) }}
                      >
                        <PlayerCellWrapper>
                          <InitialBubble>{getInitials(player.firstname, player.lastname)}</InitialBubble>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {player.firstname} {player.lastname}
                          </span>
                        </PlayerCellWrapper>
                      </ModalCell>
                      <ModalCell style={{ color: '#6B7280', fontSize: '0.8rem', fontWeight: 400 }}>{player.email}</ModalCell>
                      <ModalToggleCell>
                        <TooltipAnchor>
                          <Checkbox
                            checked={assigned}
                            disabled={assigned || !canAdd}
                            aria-label={assignedTooltip || 'Attribuer la formation'}
                            onClick={() => canAdd && assignSession(uid)}
                          />
                          {assigned && (
                            <TooltipBubble data-tooltip='true'>
                              {assignedTooltip}
                            </TooltipBubble>
                          )}
                        </TooltipAnchor>
                      </ModalToggleCell>
                    </ModalRow>
                  )
                })
              )}
            </ModalBody>

          </ModalPanel>
        </ModalOverlay>
      )}
    </Wrapper>
  )
}

export default MyFormations
